import { useEffect, useState } from 'react';
import {
  Container, Paper, Typography, TextField, Button, Box, Alert, Link, Stack, InputAdornment, IconButton, CircularProgress
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useNavigate } from 'react-router-dom';
import { lookupClinicByCode, requestToJoinClinic } from '../api/saasApi';
import logo from '../assets/logo.png';

/**
 * Join an existing clinic with the code its administrator shared. The account is
 * created immediately but sits unapproved — see PendingApproval.
 */
const Join = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ joinCode: '', name: '', email: '', password: '' });
  const [clinicName, setClinicName] = useState('');
  const [lookingUp, setLookingUp] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Resolve the code to a clinic name as it is typed, so the user can confirm
  // they're joining the right place before handing over a password.
  useEffect(() => {
    const code = form.joinCode.trim();
    if (code.length < 4) {
      setClinicName('');
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setLookingUp(true);
      try {
        const res = await lookupClinicByCode(code);
        if (cancelled) return;
        setClinicName(res.data?.succeeded === false ? '' : res.data?.data?.clinicName || '');
      } catch (e) {
        if (!cancelled) setClinicName('');
      } finally {
        if (!cancelled) setLookingUp(false);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [form.joinCode]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await requestToJoinClinic(form);
      if (res.data?.succeeded === false) {
        setError(res.data.messages?.[0] || 'Could not send your request.');
        return;
      }
      // Verify email first; the waiting screen takes over after sign-in.
      // OtpVerify reads the address from ?i= (base64), not router state.
      navigate(`/otp-verify?i=${btoa(form.email)}`);
    } catch (err) {
      setError(err?.response?.data?.messages?.[0] || 'Could not send your request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Box textAlign="center" mb={3}>
        <img src={logo} alt="Vqure" style={{ height: 48 }} />
      </Box>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>Join a clinic</Typography>
        <Typography color="text.secondary" mb={2}>
          Enter the clinic code your administrator gave you. They&apos;ll approve your request before you get access.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box component="form" onSubmit={submit}>
          <Stack spacing={2}>
            <TextField
              label="Clinic code"
              required
              value={form.joinCode}
              onChange={set('joinCode')}
              fullWidth
              autoFocus
              inputProps={{ style: { textTransform: 'uppercase', letterSpacing: 2 } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {lookingUp && <CircularProgress size={18} />}
                    {!lookingUp && clinicName && <CheckCircleIcon color="success" fontSize="small" />}
                  </InputAdornment>
                ),
              }}
              helperText={clinicName ? `Joining ${clinicName}` : ' '}
            />
            <TextField label="Your name" required value={form.name} onChange={set('name')} fullWidth />
            <TextField label="Email" type="email" required value={form.email} onChange={set('email')} fullWidth />
            <TextField
              label="Password"
              type={showPwd ? 'text' : 'password'}
              required
              value={form.password}
              onChange={set('password')}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPwd((s) => !s)}
                      edge="end"
                      size="small"
                      aria-label={showPwd ? 'Hide password' : 'Show password'}
                    >
                      {showPwd ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button type="submit" variant="contained" size="large" disabled={loading || !clinicName}>
              {loading ? 'Sending…' : 'Request to join'}
            </Button>
          </Stack>
        </Box>

        <Typography variant="body2" sx={{ mt: 2 }}>
          Don&apos;t have a code? <Link href="/signup" underline="hover">Create your own clinic</Link>
          {' · '}
          <Link href="/" underline="hover">Sign in</Link>
        </Typography>
      </Paper>
    </Container>
  );
};

export default Join;
